import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask } from 'ember-concurrency';
import type CompanyConfigurationModel from 'garaje/models/company-configuration';
import type SkinnyLocationModel from 'garaje/models/skinny-location';
import {
  GEOFENCE_RADIUS_DEFAULT,
  GEOFENCE_RADIUS_MAX,
  GEOFENCE_RADIUS_MIN,
} from 'garaje/pods/location-overview/manage/account-settings/utils/geofence-radius-bounds';
import type ProtectedController from 'garaje/pods/protected/controller';
import type AjaxService from 'garaje/services/ajax';
import type AuthzService from 'garaje/services/authz';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type InactivityService from 'garaje/services/inactivity';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import type StateService from 'garaje/services/state';
import type StatsigService from 'garaje/services/statsig';
import { AFTER_HOURS_ABSENCE_MINUTES_DEFAULT, type BusinessHoursPayload } from 'garaje/utils/presence/business-hours';
import type {
  MultiSignalPolicyPayload,
  PresenceSettingsResponse,
  SettingsPayload,
  SignalTypeAbsencePayload,
  SignoutPayload,
} from 'garaje/utils/presence/presence-settings-types';
import { Permission } from 'garaje/utils/ui-permissions';
import urlBuilder from 'garaje/utils/url-builder';
import { cached, localCopy } from 'tracked-toolbox';

interface LocationOption {
  location: SkinnyLocationModel;
  disabled: boolean;
}

enum INACTIVITY_TIME_OPTIONS {
  FIFTEEN_MINS = '15 minutes',
  THIRTY_MINS = '30 minutes',
  ONE_HR = '1 hour',
  TWO_HRS = '2 hours',
}

type MillisecondsToTimeType = {
  [key: number]: INACTIVITY_TIME_OPTIONS;
};

const MILLISECONDS_TO_TIME_MAP: MillisecondsToTimeType = {
  900000: INACTIVITY_TIME_OPTIONS.FIFTEEN_MINS,
  1800000: INACTIVITY_TIME_OPTIONS.THIRTY_MINS,
  3600000: INACTIVITY_TIME_OPTIONS.ONE_HR,
  7200000: INACTIVITY_TIME_OPTIONS.TWO_HRS,
};

// Multi-signal sign-in policy. `manual` is intentionally excluded — only automatic
// signals can corroborate a multi-signal sign-in. A policy needs at least this many required
// types to be meaningful (matches the backend validation).
const MIN_MULTI_SIGNAL_REQUIRED_TYPES = 2;

class LocationOverviewManageAccountSettingsController extends Controller {
  @service declare ajax: AjaxService;
  @service declare abilities: AbilitiesService;
  @service declare authz: AuthzService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare inactivity: InactivityService;
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare state: StateService;
  @service declare statsig: StatsigService;
  @service declare store: Store;

  @controller('protected') declare protectedController: ProtectedController;

  @tracked isInactivityBoxOpen: boolean = false;
  @localCopy('model.sessionInactivityTimeout', null) declare inactivityTimeout: number | null;

  @tracked isPresenceControlCenterOpen: boolean = false;
  @tracked selectedLocationId: string | null = null;
  @tracked macMappingsSearch = '';
  @tracked macMappingsSource = '';

  @tracked badgeEnabled: boolean = false;
  @tracked wifiEnabled: boolean = false;
  @tracked ssoEnabled: boolean = false;
  @tracked geoLocationEnabled: boolean = false;
  @tracked manualEnabled: boolean = false;
  @tracked mdmEnabled: boolean = false;
  // After-hours sign-in validation (EIP-2017): a single toggle that allows/blocks auto sign-in for
  // signals occurring outside the location's business hours. Defaults true (allowed = today's
  // behavior); when off, the milestone-1 UI writes `enable-after-hours-sign-in: false` to every
  // signal type. Only meaningful when business hours is configured (see `businessHoursConfigured`).
  @tracked enableAfterHoursSignIn: boolean = true;
  @tracked isLoading: boolean = false;
  @tracked error: string | null = null;
  @tracked selectedPresenceTab: 'sign-in' | 'sign-out' = 'sign-in';

  // Multi-signal sign-in policy. `signInMode` is the mutually-exclusive selector on the
  // Sign-in tab between today's per-signal behavior ('any') and the corroboration policy
  // ('multi-signal'); it maps to the stored policy's `enabled` flag. The required-type toggles below
  // and the window are the policy's own draft state, kept independent of the per-signal `settings`
  // toggles above so switching modes preserves each side's selection. Manual is not a required type.
  @tracked signInMode: 'any' | 'multi-signal' = 'any';
  @tracked multiSignalBadgeRequired: boolean = false;
  @tracked multiSignalWifiRequired: boolean = false;
  @tracked multiSignalSsoRequired: boolean = false;
  @tracked multiSignalMdmRequired: boolean = false;
  @tracked multiSignalGeoLocationRequired: boolean = false;
  // The corroboration window in minutes; null = "unset" (required before an enabled policy can save).
  @tracked multiSignalWindowMinutes: number | null = null;
  // Snapshot of the last saved/loaded policy (API shape), driving the dirty-diff like the trio below.
  @tracked persistedMultiSignalPolicy: MultiSignalPolicyPayload | null = null;

  // Aggregated auto sign-out configuration (any signal resets a single timer)
  @tracked absenceOfSignalsEnabled: boolean = false;
  @tracked absenceOfSignalsDuration: number = 3600000; // Default: 1 hour

  // Per-signal-type auto sign-out configuration (in UI order: badge, wifi, sso, mdm, geo-location)
  @tracked badgeAutoSignOutEnabled: boolean = false;
  @tracked badgeAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked wifiAutoSignOutEnabled: boolean = false;
  @tracked wifiAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked ssoAutoSignOutEnabled: boolean = false;
  @tracked ssoAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked mdmAutoSignOutEnabled: boolean = false;
  @tracked mdmAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked geoLocationAutoSignOutEnabled: boolean = false;

  // After-hours auto sign-out (EIP-2017): opt-in + interval (minutes) applied to signals outside the
  // business-hours window. Only meaningful once business hours is configured (the toggle is disabled
  // otherwise — see `businessHoursConfigured`). Default off / 60 min, matching the server default.
  @tracked afterHoursAutoSignOutEnabled: boolean = false;
  @tracked afterHoursAbsenceMinutes: number = AFTER_HOURS_ABSENCE_MINUTES_DEFAULT;

  // Business hours is configured on the per-location Location settings page now, not here.
  // We still load it (read-only) into this snapshot because the Sign-in / Sign-out after-hours toggles
  // depend on it (see `businessHoursConfigured`) and every tab's Save must carry the persisted
  // business-hours block through the unified PUT so it isn't cleared.
  //
  // The Sign-in / Sign-out tabs use an explicit draft → Save flow: the tracked fields above are the
  // editable drafts, and nothing is persisted until that tab's Save runs. The `persisted*` snapshots
  // below hold each tab's last saved/loaded value (API shape). On any tab's Save we send the full
  // document built from that tab's draft plus the *other* tab's persisted snapshot — so unsaved edits
  // on a different tab are never promoted and never leak into the PUT.
  @tracked persistedBusinessHours: BusinessHoursPayload | null = null;
  @tracked persistedSignIn: SettingsPayload | null = null;
  @tracked persistedSignOut: SignoutPayload | null = null;

  // Location-level "Automatic sign-out" (time-of-day) fallback — lives on the full Location
  // model as `autoSignOutAtMinutesSinceMidnight` (enabled ⇔ non-null, matching
  // components/location-settings/sign-out/auto-sign-out-time.gts). Used only to surface a
  // fallback-explanation tooltip on the PCC Sign-Out tab.
  @tracked locationAutoSignOutEnabled: boolean = false;

  // Per-location geofence radius (meters) — Unit 4 of EIP-3334.
  // Display default of 150 m matches the existing mobile fallback so behavior is unchanged
  // for locations whose server-side column is still NULL. Bounds mirror the server CHECK
  // constraint exactly (see utils/geofence-radius-bounds.ts).
  @tracked geoLocationRadius: number = GEOFENCE_RADIUS_DEFAULT;
  @tracked geoLocationRadiusError: string | null = null;

  declare model: CompanyConfigurationModel | undefined;

  get headerTitle(): string {
    const companyName = this.state.currentCompany?.name;
    return companyName ? `Account settings for ${companyName}` : 'Account settings';
  }

  get inactivityDescription(): INACTIVITY_TIME_OPTIONS | undefined {
    const sessionTimeout: number = this.model?.sessionInactivityTimeout || 7200000;
    return MILLISECONDS_TO_TIME_MAP[sessionTimeout];
  }

  get isInactivityEnabled(): boolean {
    const companyConfiguration = this.model;
    return Boolean(companyConfiguration?.sessionInactivityTimeout);
  }

  get inactivityOptions(): Array<number> {
    return [900000, 1800000, 3600000, 7200000];
  }

  get isSaveBtnDisabled(): boolean {
    return this.model?.sessionInactivityTimeout === this.inactivityTimeout;
  }

  @cached
  get availableLocations(): LocationOption[] {
    const locations = this.skinnyLocations.currentCompanyLocations || [];

    // Map locations to create copy of location with 'disabled' property updated
    // PowerSelect checks 'disabled' by default and grays out options
    // disabled_to_employees_at (not 'disabled') controls employee visibility
    return locations.map((location) => ({
      location,
      disabled: !!location.disabledToEmployeesAt,
    }));
  }

  get selectedLocation(): LocationOption | null {
    if (!this.selectedLocationId) return null;
    const options = this.availableLocations;

    return options.find((option) => option.location.id === this.selectedLocationId) || null;
  }

  get shouldShowToggles(): boolean {
    return !!this.selectedLocationId;
  }

  get isInactivityTimeoutEnabled(): boolean {
    return !!this.state.features?.canAccessInactivityLogout;
  }

  get isPresenceControlCenterEnabled(): boolean {
    return this.authz.hasPermissionAtCurrentCompany(Permission.ACCOUNT_MANAGEMENT_PRODUCT_SETTINGS_READ);
  }

  get isGeofenceRadiusEnabled(): boolean {
    return this.featureFlags.isEnabled('presence-control-center-geofence-radius');
  }

  // Gates the business-hours UI only (Location settings tab + the sign-out tab's Business hours
  // section). Save logic stays unconditional, so a draft saved before the flag flips off still persists.
  get isBusinessHoursEnabled(): boolean {
    return this.featureFlags.isEnabled('presence-control-center-business-hours');
  }

  // Gates the multi-signal sign-in policy UI. Kept dark until the backend corroboration engine
  // that enforces the policy is live — otherwise an enabled policy would silently do nothing. When
  // off, the Sign-in tab shows only today's per-signal toggles and a Save omits the policy block
  // entirely (the backend keeps any stored value).
  get isMultiSignalPolicyEnabled(): boolean {
    return this.featureFlags.isEnabled('presence-control-center-multi-signal-policy');
  }

  // Bounds + default re-exposed to the template — keeps the values in a single source
  // (utils/geofence-radius-bounds.ts) and out of the .hbs file.
  get geofenceRadiusMin(): number {
    return GEOFENCE_RADIUS_MIN;
  }

  get geofenceRadiusMax(): number {
    return GEOFENCE_RADIUS_MAX;
  }

  get geofenceRadiusDefault(): number {
    return GEOFENCE_RADIUS_DEFAULT;
  }

  get showMacAddressMappings(): boolean {
    return this.abilities.can('view mac-address-mapping');
  }

  get companyId(): string {
    return this.state.currentCompany?.id ?? '';
  }

  get anySignOutSettingEnabled(): boolean {
    return (
      this.absenceOfSignalsEnabled ||
      this.badgeAutoSignOutEnabled ||
      this.wifiAutoSignOutEnabled ||
      this.ssoAutoSignOutEnabled ||
      this.mdmAutoSignOutEnabled ||
      this.geoLocationAutoSignOutEnabled
    );
  }

  // Notice explains that, when PCC will sign employees out on signal absence AND the
  // location also has time-based Automatic sign-out on, the location setting is a fallback.
  get showSignOutFallbackNotice(): boolean {
    return this.locationAutoSignOutEnabled && this.anySignOutSettingEnabled;
  }

  get absenceOfSignalsDurationOptions(): Array<{ value: number; label: string }> {
    return [
      { value: 3600000, label: '1 hour' },
      { value: 7200000, label: '2 hours' },
      { value: 14400000, label: '4 hours' },
      { value: 28800000, label: '8 hours' },
    ];
  }

  get selectedAbsenceOfSignalsDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.absenceOfSignalsDuration);
  }

  get selectedBadgeDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.badgeAutoSignOutDuration);
  }

  get selectedWifiDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.wifiAutoSignOutDuration);
  }

  get selectedSsoDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.ssoAutoSignOutDuration);
  }

  get selectedMdmDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.mdmAutoSignOutDuration);
  }

  // Business hours is "configured" once it's been saved with the master switch on — the only state
  // in which after-hours auto sign-out can take effect (entry-recorder no-ops it otherwise). Gated on
  // the *persisted* config (not the unsaved draft) because the after-hours toggle auto-saves and
  // should reflect what's actually in effect. Shared gate for the after-hours sign-out toggle here and
  // the future after-hours sign-in toggle.
  get businessHoursConfigured(): boolean {
    return this.persistedBusinessHours !== null && this.persistedBusinessHours.enabled;
  }

  // After-hours interval options. Stored as minutes (unlike the signal-absence durations above, which
  // are milliseconds), matching the `after-hours-absence-minutes` wire format.
  get afterHoursAbsenceMinutesOptions(): Array<{ value: number; label: string }> {
    return [
      { value: 15, label: '15 minutes' },
      { value: 30, label: '30 minutes' },
      { value: 60, label: '1 hour' },
      { value: 120, label: '2 hours' },
      { value: 240, label: '4 hours' },
      { value: 480, label: '8 hours' },
    ];
  }

  get selectedAfterHoursAbsenceMinutes(): { value: number; label: string } | undefined {
    return this.afterHoursAbsenceMinutesOptions.find((option) => option.value === this.afterHoursAbsenceMinutes);
  }

  // ---- Sign-in / Sign-out draft → Save -----------------------------------------------------------

  // The after-hours sign-in value as persisted: forced to "allowed" (true) unless business hours is
  // configured (the toggle is disabled then), so a block that can't take effect is never written —
  // mirrors the after-hours auto sign-out handling in signOutDraft. Shared by the draft and the
  // Multi-signal save overlay so both modes persist the same value.
  get afterHoursSignInForSave(): boolean {
    return this.businessHoursConfigured ? this.enableAfterHoursSignIn : true;
  }

  // The Sign-in `settings` object built from the toggle drafts. The geo-location radius is included
  // only when the feature flag is on (matching the PUT contract / persisted snapshot).
  get signInDraft(): SettingsPayload {
    // The single after-hours toggle fans its value out to every signal type.
    const afterHours = this.afterHoursSignInForSave;
    const geoLocationEntry: SettingsPayload['geo-location'] = {
      enabled: this.geoLocationEnabled,
      schedule: null,
      'enable-after-hours-sign-in': afterHours,
    };
    if (this.isGeofenceRadiusEnabled) {
      geoLocationEntry['geofence-radius-meters'] = this.geoLocationRadius;
    }
    return {
      badge: { enabled: this.badgeEnabled, schedule: null, 'enable-after-hours-sign-in': afterHours },
      wifi: { enabled: this.wifiEnabled, schedule: null, 'enable-after-hours-sign-in': afterHours },
      sso: { enabled: this.ssoEnabled, schedule: null, 'enable-after-hours-sign-in': afterHours },
      'geo-location': geoLocationEntry,
      manual: { enabled: this.manualEnabled, schedule: null, 'enable-after-hours-sign-in': afterHours },
      mdm: { enabled: this.mdmEnabled, schedule: null, 'enable-after-hours-sign-in': afterHours },
    };
  }

  // The Sign-out `signout-settings` object built from the aggregated / per-signal / after-hours drafts.
  get signOutDraft(): SignoutPayload {
    const signalTypeAbsence: SignalTypeAbsencePayload = {};
    if (this.badgeAutoSignOutEnabled) signalTypeAbsence.badge = Math.floor(this.badgeAutoSignOutDuration / 60000);
    if (this.wifiAutoSignOutEnabled) signalTypeAbsence.wifi = Math.floor(this.wifiAutoSignOutDuration / 60000);
    if (this.ssoAutoSignOutEnabled) signalTypeAbsence.sso = Math.floor(this.ssoAutoSignOutDuration / 60000);
    if (this.mdmAutoSignOutEnabled) signalTypeAbsence.mdm = Math.floor(this.mdmAutoSignOutDuration / 60000);
    if (this.geoLocationAutoSignOutEnabled) signalTypeAbsence['geo-location'] = 60; // unused by entry-recorder for active sign-out

    const anyPerSignalTypeEnabled =
      this.badgeAutoSignOutEnabled ||
      this.wifiAutoSignOutEnabled ||
      this.ssoAutoSignOutEnabled ||
      this.mdmAutoSignOutEnabled ||
      this.geoLocationAutoSignOutEnabled;
    const anyAutoSignOutEnabled = this.absenceOfSignalsEnabled || anyPerSignalTypeEnabled;

    // Force the after-hours opt-in off unless business hours is configured (the toggle is disabled
    // then), so we never persist an opt-in that can't take effect.
    const afterHoursEnabled = this.businessHoursConfigured && this.afterHoursAutoSignOutEnabled;

    return {
      enabled: anyAutoSignOutEnabled,
      'signal-absence-minutes': this.absenceOfSignalsEnabled ? Math.floor(this.absenceOfSignalsDuration / 60000) : null,
      'signal-type-absence': Object.keys(signalTypeAbsence).length > 0 ? signalTypeAbsence : null,
      'enable-after-hours-auto-sign-out': afterHoursEnabled,
      'after-hours-absence-minutes': afterHoursEnabled
        ? this.afterHoursAbsenceMinutes
        : AFTER_HOURS_ABSENCE_MINUTES_DEFAULT,
    };
  }

  // Dirty = draft differs from the last saved/loaded snapshot. Both sides come from the same draft
  // getter (the snapshot is captured from it), so the key order is stable for the JSON compare.
  get signInDirty(): boolean {
    return JSON.stringify(this.signInDraft) !== JSON.stringify(this.persistedSignIn);
  }

  // The after-hours toggle is editable in BOTH sign-in modes, but the per-signal `settings` draft only
  // drives the dirty-diff in Allow-any mode (Multi-signal saves send the persisted snapshot). Track
  // after-hours dirtiness explicitly — against the value on the persisted snapshot, which the UI writes
  // uniformly to every signal — so a lone toggle change can be saved in Multi-signal mode too.
  get afterHoursSignInDirty(): boolean {
    const persisted = this.persistedSignIn?.badge?.['enable-after-hours-sign-in'] ?? true;
    return this.afterHoursSignInForSave !== persisted;
  }

  // Overlay the current after-hours value onto a per-signal settings payload, leaving every other field
  // (enabled, geofence radius) untouched. Used for Multi-signal saves, where the per-signal settings
  // come from the persisted snapshot (not the live draft) but the after-hours toggle is still editable.
  settingsWithAfterHours(settings: SettingsPayload): SettingsPayload {
    const v = this.afterHoursSignInForSave;
    return {
      badge: { ...settings.badge, 'enable-after-hours-sign-in': v },
      wifi: { ...settings.wifi, 'enable-after-hours-sign-in': v },
      sso: { ...settings.sso, 'enable-after-hours-sign-in': v },
      'geo-location': { ...settings['geo-location'], 'enable-after-hours-sign-in': v },
      manual: { ...settings.manual, 'enable-after-hours-sign-in': v },
      mdm: { ...settings.mdm, 'enable-after-hours-sign-in': v },
    };
  }

  get signOutDirty(): boolean {
    return JSON.stringify(this.signOutDraft) !== JSON.stringify(this.persistedSignOut);
  }

  // Validation is mode-specific. Allow-any mode validates the geofence radius input — the only
  // editable Sign-in input that can be invalid (and, since the radius is stored on the location's
  // presence settings, it is configured here only, never from Multi-signal mode). Multi-signal mode
  // validates the policy.
  // Each error only blocks Save while its input is rendered: the radius row exists only in Allow-any
  // mode, so a stale radius error can't keep the (radius-less) Multi-signal form unsaveable.
  get signInValid(): boolean {
    if (this.signInMode === 'multi-signal') return this.multiSignalPolicyValid;
    return !(this.isGeofenceRadiusEnabled && this.geoLocationEnabled && Boolean(this.geoLocationRadiusError));
  }

  // Every Sign-out input is a constrained dropdown, so the draft is always valid.
  get signOutValid(): boolean {
    return true;
  }

  get canSaveSignIn(): boolean {
    // Per-signal settings (incl. geofence radius) are editable only in Allow-any mode, so their
    // dirtiness enables Save only there; Multi-signal mode saves the policy alone. (Switching modes
    // flips the policy's `enabled`, so a mode change is always captured by multiSignalPolicyDirty.)
    // The after-hours toggle is editable in both modes, so its dirtiness enables Save in either.
    const settingsDirty = this.signInMode === 'any' && this.signInDirty;
    return (settingsDirty || this.afterHoursSignInDirty || this.multiSignalPolicyDirty) && this.signInValid;
  }

  get signInSaveDisabled(): boolean {
    return !this.canSaveSignIn;
  }

  // ---- Multi-signal sign-in policy, gated into the Sign-in tab's Save above ----------------------

  // Styling for each mode tile — the selected tile gets the brand border at full strength; the other
  // is dimmed (gray border + reduced opacity) so the active choice reads clearly. The title gets a
  // matching emphasis (bold default heading color when selected, muted otherwise). (Kept in the controller because
  // hbs class attributes can't carry single-quoted inline `if` string args.)
  get allowAnyTileClass(): string {
    return this.signInMode === 'any' ? 'border-gem-50' : 'border-gray-200 opacity-75';
  }

  get multiSignalTileClass(): string {
    return this.signInMode === 'multi-signal' ? 'border-gem-50' : 'border-gray-200 opacity-75';
  }

  get allowAnyTitleClass(): string {
    return this.signInMode === 'any' ? 'font-bold' : 'font-semibold text-gray-500';
  }

  get multiSignalTitleClass(): string {
    return this.signInMode === 'multi-signal' ? 'font-bold' : 'font-semibold text-gray-500';
  }

  // Selectable corroboration windows (minutes on the wire — distinct from the ms-based sign-out
  // durations). Mirrors the absence-of-signals dropdown shape so the template reuses the same pattern.
  get multiSignalWindowOptions(): Array<{ value: number; label: string }> {
    return [
      { value: 15, label: '15 minutes' },
      { value: 30, label: '30 minutes' },
      { value: 60, label: '1 hour' },
      { value: 120, label: '2 hours' },
      { value: 240, label: '4 hours' },
    ];
  }

  get selectedMultiSignalWindow(): { value: number; label: string } | undefined {
    return this.multiSignalWindowOptions.find((option) => option.value === this.multiSignalWindowMinutes);
  }

  // The required-type set assembled from the per-type toggles, in canonical order. Manual is never
  // included (only entry-recorder auto-signals corroborate).
  get multiSignalRequiredTypes(): string[] {
    const types: string[] = [];
    if (this.multiSignalBadgeRequired) types.push('badge');
    if (this.multiSignalWifiRequired) types.push('wifi');
    if (this.multiSignalSsoRequired) types.push('sso');
    if (this.multiSignalMdmRequired) types.push('mdm');
    if (this.multiSignalGeoLocationRequired) types.push('geo-location');
    return types;
  }

  // The `multi-signal-policy` block built from the draft. `enabled` mirrors the mode selector; the
  // required types and window are always carried (so switching to 'any' preserves them for re-enable).
  get multiSignalPolicyDraft(): MultiSignalPolicyPayload {
    return {
      enabled: this.signInMode === 'multi-signal',
      'required-signal-types': this.multiSignalRequiredTypes,
      'corroboration-window-minutes': this.multiSignalWindowMinutes,
    };
  }

  // In multi-signal mode the policy must be fully configured before Save: >= 2 required types and a
  // window. (The BE enforces the same — this just gates the button and avoids a round-trip.) In 'any'
  // mode there's nothing to validate.
  get multiSignalPolicyValid(): boolean {
    if (this.signInMode !== 'multi-signal') return true;
    return (
      this.multiSignalRequiredTypes.length >= MIN_MULTI_SIGNAL_REQUIRED_TYPES && this.multiSignalWindowMinutes != null
    );
  }

  get multiSignalPolicyDirty(): boolean {
    return JSON.stringify(this.multiSignalPolicyDraft) !== JSON.stringify(this.persistedMultiSignalPolicy);
  }

  get canSaveSignOut(): boolean {
    return this.signOutDirty && this.signOutValid;
  }

  get signOutSaveDisabled(): boolean {
    return !this.canSaveSignOut;
  }

  // Seed the persisted snapshot from the API's business-hours block (read-only — business hours is
  // edited on the per-location Location settings page now). Drives `businessHoursConfigured` (which
  // gates the Sign-in/Sign-out after-hours toggles) and the value carried through each tab's Save.
  private loadBusinessHours(businessHours?: PresenceSettingsResponse['data']['attributes']['business-hours']): void {
    this.persistedBusinessHours = businessHours
      ? {
          enabled: businessHours.enabled,
          'after-hours-report-enabled': businessHours['after-hours-report-enabled'] ?? false,
          days: businessHours.days ?? {},
        }
      : null;
  }

  displayTimeoutPeriod(timeout: number): string {
    return MILLISECONDS_TO_TIME_MAP[timeout] || '';
  }

  @action
  openInactivityBox(): void {
    this.isInactivityBoxOpen = true;
  }

  @action
  closeInactivityBox(): void {
    this.isInactivityBoxOpen = false;
    this.inactivityTimeout = this.model?.sessionInactivityTimeout || null;
  }

  @action
  togglePresenceControlCenter(): void {
    this.isPresenceControlCenterOpen = !this.isPresenceControlCenterOpen;
  }

  @action
  selectPresenceTab(tabName: 'sign-in' | 'sign-out'): void {
    this.selectedPresenceTab = tabName;
  }

  @action
  async selectLocation(option: LocationOption): Promise<void> {
    this.selectedLocationId = option.location.id;
    await this.loadPresenceSettings(this.selectedLocationId);
    await this.loadLocationAutoSignOut(this.selectedLocationId);
  }

  // Deep-link to the selected location's "Automatic sign-out" settings. We can't rely on a
  // <LinkTo> with a `location` query param — the application route only reads that param on a
  // full page boot, not on in-app transitions — so we switch the current location and
  // transition the same way the locations table's "View settings" link does.
  @action
  viewLocationSettings(): void {
    if (!this.selectedLocationId) return;
    this.protectedController.send('switchLocation', this.selectedLocationId, 'manage.location-settings');
  }

  // Deep-link like viewLocationSettings, but also scroll to the Business hours panel — used by the
  // Sign-in / Sign-out tabs' "Configure business hours" links. The scrollTo query param is consumed by
  // the location-settings index route (scrolls to the element with id="business-hours").
  @action
  configureBusinessHours(): void {
    if (!this.selectedLocationId) return;
    this.protectedController.send('switchLocation', this.selectedLocationId, 'manage.location-settings', {
      queryParams: { scrollTo: 'business-hours' },
    });
  }

  // Sign-in / Sign-out controls mutate draft state only; nothing is persisted until that tab's Save.

  @action
  toggleBadge(enabled: boolean): void {
    this.badgeEnabled = enabled;
  }

  @action
  toggleWifi(enabled: boolean): void {
    this.wifiEnabled = enabled;
  }

  @action
  toggleSSO(enabled: boolean): void {
    this.ssoEnabled = enabled;
  }

  @action
  toggleGeoLocation(enabled: boolean): void {
    this.geoLocationEnabled = enabled;
  }

  @action
  toggleManual(enabled: boolean): void {
    this.manualEnabled = enabled;
  }

  @action
  toggleMDM(enabled: boolean): void {
    this.mdmEnabled = enabled;
  }

  @action
  toggleEnableAfterHoursSignIn(enabled: boolean): void {
    this.enableAfterHoursSignIn = enabled;
  }

  // ---- Multi-signal sign-in policy controls (draft only; persisted on the Sign-in tab's Save) -----

  @action
  selectSignInMode(mode: 'any' | 'multi-signal'): void {
    this.signInMode = mode;
  }

  @action
  toggleMultiSignalBadge(enabled: boolean): void {
    this.multiSignalBadgeRequired = enabled;
  }

  @action
  toggleMultiSignalWifi(enabled: boolean): void {
    this.multiSignalWifiRequired = enabled;
  }

  @action
  toggleMultiSignalSSO(enabled: boolean): void {
    this.multiSignalSsoRequired = enabled;
  }

  @action
  toggleMultiSignalMDM(enabled: boolean): void {
    this.multiSignalMdmRequired = enabled;
  }

  @action
  toggleMultiSignalGeoLocation(enabled: boolean): void {
    this.multiSignalGeoLocationRequired = enabled;
  }

  @action
  selectMultiSignalWindow(option: { value: number; label: string }): void {
    this.multiSignalWindowMinutes = option.value;
  }

  // Required by SlideToggle's signature for the always-disabled Manual row in multi-signal mode.
  @action
  noopToggle(): void {}

  @action
  toggleAbsenceOfSignals(enabled: boolean): void {
    this.absenceOfSignalsEnabled = enabled;
  }

  @action
  selectAbsenceOfSignalsDuration(option: { value: number; label: string }): void {
    this.absenceOfSignalsDuration = option.value;
  }

  @action
  toggleBadgeAutoSignOut(enabled: boolean): void {
    this.badgeAutoSignOutEnabled = enabled;
  }

  @action
  selectBadgeDuration(option: { value: number; label: string }): void {
    this.badgeAutoSignOutDuration = option.value;
  }

  @action
  toggleWifiAutoSignOut(enabled: boolean): void {
    this.wifiAutoSignOutEnabled = enabled;
  }

  @action
  selectWifiDuration(option: { value: number; label: string }): void {
    this.wifiAutoSignOutDuration = option.value;
  }

  @action
  toggleSsoAutoSignOut(enabled: boolean): void {
    this.ssoAutoSignOutEnabled = enabled;
  }

  @action
  selectSsoDuration(option: { value: number; label: string }): void {
    this.ssoAutoSignOutDuration = option.value;
  }

  @action
  toggleMdmAutoSignOut(enabled: boolean): void {
    this.mdmAutoSignOutEnabled = enabled;
  }

  @action
  selectMdmDuration(option: { value: number; label: string }): void {
    this.mdmAutoSignOutDuration = option.value;
  }

  @action
  toggleGeoLocationAutoSignOut(enabled: boolean): void {
    this.geoLocationAutoSignOutEnabled = enabled;
  }

  @action
  toggleAfterHoursAutoSignOut(enabled: boolean): void {
    this.afterHoursAutoSignOutEnabled = enabled;
  }

  @action
  selectAfterHoursAbsenceMinutes(option: { value: number; label: string }): void {
    this.afterHoursAbsenceMinutes = option.value;
  }

  @action
  validateGeofenceRadius(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = target.value.trim();
    const errorMsg = `Radius must be a whole number between ${GEOFENCE_RADIUS_MIN} and ${GEOFENCE_RADIUS_MAX} meters`;
    if (raw === '' || !/^-?\d+$/.test(raw)) {
      this.geoLocationRadiusError = errorMsg;
      return;
    }
    const value = Number(raw);
    if (value < GEOFENCE_RADIUS_MIN || value > GEOFENCE_RADIUS_MAX) {
      this.geoLocationRadiusError = errorMsg;
      return;
    }
    this.geoLocationRadius = value;
    this.geoLocationRadiusError = null;
  }

  // Persist just the Sign-in tab: sends the Sign-in draft plus the other tabs' persisted snapshots.
  @action
  async saveSignIn(): Promise<void> {
    if (!this.canSaveSignIn) return;
    await this.savePresenceSettings('sign-in');
  }

  // Persist just the Sign-out tab: sends the Sign-out draft plus the other tabs' persisted snapshots.
  @action
  async saveSignOut(): Promise<void> {
    if (!this.canSaveSignOut) return;
    await this.savePresenceSettings('sign-out');
  }

  enableInactivityTask = dropTask(async () => {
    const companyConfiguration = this.model;
    try {
      if (companyConfiguration) {
        this.inactivityTimeout = 7200000;
        companyConfiguration.sessionInactivityTimeout = this.inactivityTimeout;
        await companyConfiguration.save();

        // start the timer
        await this.inactivity.initialize();
        this.flashMessages.showAndHideFlash('success', 'Inactivity based timeout enabled');
        this.openInactivityBox();
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong, please try again');
    }
  });

  disableInactivityTask = dropTask(async () => {
    const companyConfiguration = this.model;
    try {
      if (companyConfiguration) {
        this.inactivityTimeout = null;
        companyConfiguration.sessionInactivityTimeout = this.inactivityTimeout;
        await companyConfiguration.save();

        // stop the timer
        this.inactivity.cleanUp();
        this.flashMessages.showAndHideFlash('success', 'Inactivity based timeout disabled');
        this.isInactivityBoxOpen = false;
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong, please try again');
    }
  });

  saveInactivityTask = dropTask(async () => {
    const companyConfiguration = this.model;
    try {
      if (companyConfiguration) {
        companyConfiguration.sessionInactivityTimeout = this.inactivityTimeout;
        await companyConfiguration.save();
        // change the timeout duration
        this.inactivity.timeoutDuration = this.inactivityTimeout;
        this.flashMessages.showAndHideFlash('success', 'Inactivity timeout period updated');
        this.isInactivityBoxOpen = false;
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong, please try again');
    }
  });

  async loadPresenceSettings(locationId: string): Promise<void> {
    if (!locationId) return;

    try {
      this.isLoading = true;
      this.error = null;

      const url = urlBuilder.presenceSettingsUrl(locationId);

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      const response = (await this.ajax.request(url, {
        method: 'GET',
      })) as PresenceSettingsResponse;

      // Map API response to UI state
      const settings = response.data?.attributes?.settings || {};

      this.badgeEnabled = settings.badge?.enabled ?? true;
      this.wifiEnabled = settings.wifi?.enabled ?? true;
      this.ssoEnabled = settings.sso?.enabled ?? true;
      this.geoLocationEnabled = settings['geo-location']?.enabled ?? true;
      this.manualEnabled = settings.manual?.enabled ?? true;
      this.mdmEnabled = settings.mdm?.enabled ?? true;

      // After-hours sign-in is a single toggle written uniformly to every signal type, but read it
      // defensively: reflect a block (false) if ANY present signal has it disabled, so a location
      // where one signal is absent but another is blocked doesn't silently default to allowed and
      // re-enable after-hours sign-in on the next save. Defaults true (allowed) when no signal
      // specifies it — older rows / locations that never set it keep today's behavior.
      const signalEntries = [
        settings.badge,
        settings.wifi,
        settings.sso,
        settings['geo-location'],
        settings.manual,
        settings.mdm,
      ];
      this.enableAfterHoursSignIn = !signalEntries.some((s) => s?.['enable-after-hours-sign-in'] === false);

      // Geofence radius — only overwrite the tracked default when the API surfaces a real
      // value. `null` (column unset on the server) means "use the display default 150".
      const apiRadius = settings['geo-location']?.['geofence-radius-meters'];
      if (apiRadius != null) {
        this.geoLocationRadius = apiRadius;
      } else {
        this.geoLocationRadius = GEOFENCE_RADIUS_DEFAULT;
      }
      this.geoLocationRadiusError = null;

      // Load sign-out settings
      const signoutSettings = response.data?.attributes?.['signout-settings'];
      const signalTypeAbsence = signoutSettings?.['signal-type-absence'];
      const signalAbsenceMinutes = signoutSettings?.['signal-absence-minutes'];

      // Load per-day business hours (top-level, location-level — not under signout-settings).
      // Seed both the per-day drafts and the persisted snapshot so the section starts clean
      // (not dirty) and auto-saves from other controls preserve the stored config.
      this.loadBusinessHours(response.data?.attributes?.['business-hours']);

      // Load after-hours auto sign-out (EIP-2017). Opt-in + interval, defaulting off / 60.
      this.afterHoursAutoSignOutEnabled = signoutSettings?.['enable-after-hours-auto-sign-out'] ?? false;
      this.afterHoursAbsenceMinutes =
        signoutSettings?.['after-hours-absence-minutes'] ?? AFTER_HOURS_ABSENCE_MINUTES_DEFAULT;

      // Load aggregated settings (independent of per-signal-type)
      if (signalAbsenceMinutes != null) {
        this.absenceOfSignalsEnabled = signoutSettings?.enabled ?? false;
        this.absenceOfSignalsDuration = signalAbsenceMinutes * 60000;
      } else {
        this.absenceOfSignalsEnabled = false;
        this.absenceOfSignalsDuration = 3600000;
      }

      // Load per-signal-type settings (independent of aggregated)
      if (signalTypeAbsence) {
        this.badgeAutoSignOutEnabled = signalTypeAbsence.badge != null;
        this.badgeAutoSignOutDuration = signalTypeAbsence.badge ? signalTypeAbsence.badge * 60000 : 3600000;

        this.wifiAutoSignOutEnabled = signalTypeAbsence.wifi != null;
        this.wifiAutoSignOutDuration = signalTypeAbsence.wifi ? signalTypeAbsence.wifi * 60000 : 3600000;

        this.ssoAutoSignOutEnabled = signalTypeAbsence.sso != null;
        this.ssoAutoSignOutDuration = signalTypeAbsence.sso ? signalTypeAbsence.sso * 60000 : 3600000;

        this.mdmAutoSignOutEnabled = signalTypeAbsence.mdm != null;
        this.mdmAutoSignOutDuration = signalTypeAbsence.mdm ? signalTypeAbsence.mdm * 60000 : 3600000;

        this.geoLocationAutoSignOutEnabled = signalTypeAbsence['geo-location'] != null;
      } else {
        // Default to OFF - user must opt-in to per-signal-type
        this.badgeAutoSignOutEnabled = false;
        this.badgeAutoSignOutDuration = 3600000;

        this.wifiAutoSignOutEnabled = false;
        this.wifiAutoSignOutDuration = 3600000;

        this.ssoAutoSignOutEnabled = false;
        this.ssoAutoSignOutDuration = 3600000;

        this.mdmAutoSignOutEnabled = false;
        this.mdmAutoSignOutDuration = 3600000;

        this.geoLocationAutoSignOutEnabled = false;
      }

      // Load the multi-signal sign-in policy. Absent (or disabled) ⇒ 'any' mode; the
      // required-type toggles default off and the window unset, matching a location with no policy.
      const multiSignalPolicy = response.data?.attributes?.['multi-signal-policy'];
      const requiredTypes = new Set(multiSignalPolicy?.['required-signal-types'] ?? []);
      this.signInMode = multiSignalPolicy?.enabled ? 'multi-signal' : 'any';
      this.multiSignalBadgeRequired = requiredTypes.has('badge');
      this.multiSignalWifiRequired = requiredTypes.has('wifi');
      this.multiSignalSsoRequired = requiredTypes.has('sso');
      this.multiSignalMdmRequired = requiredTypes.has('mdm');
      this.multiSignalGeoLocationRequired = requiredTypes.has('geo-location');
      this.multiSignalWindowMinutes = multiSignalPolicy?.['corroboration-window-minutes'] ?? null;

      // Capture the loaded state as each tab's persisted snapshot so all three tabs start not-dirty.
      // (loadBusinessHours already set persistedBusinessHours.) Built from the same draft getters the
      // dirty-diff and Save use, so they compare equal until the admin edits something.
      this.persistedSignIn = this.signInDraft;
      this.persistedSignOut = this.signOutDraft;
      this.persistedMultiSignalPolicy = this.multiSignalPolicyDraft;
    } catch (_error) {
      this.error = 'Failed to load presence settings';
      this.flashMessages.showAndHideFlash('error', 'Failed to load presence settings');
      // Reset to defaults on error
      this.badgeEnabled = false;
      this.wifiEnabled = false;
      this.ssoEnabled = false;
      this.geoLocationEnabled = false;
      this.manualEnabled = false;
      this.mdmEnabled = false;
      this.enableAfterHoursSignIn = true;

      this.badgeAutoSignOutEnabled = false;
      this.badgeAutoSignOutDuration = 3600000;
      this.wifiAutoSignOutEnabled = false;
      this.wifiAutoSignOutDuration = 3600000;
      this.ssoAutoSignOutEnabled = false;
      this.ssoAutoSignOutDuration = 3600000;
      this.mdmAutoSignOutEnabled = false;
      this.mdmAutoSignOutDuration = 3600000;
      this.geoLocationAutoSignOutEnabled = false;

      this.absenceOfSignalsEnabled = false;
      this.absenceOfSignalsDuration = 3600000;

      this.afterHoursAutoSignOutEnabled = false;
      this.afterHoursAbsenceMinutes = AFTER_HOURS_ABSENCE_MINUTES_DEFAULT;

      this.loadBusinessHours(undefined);

      this.geoLocationRadius = GEOFENCE_RADIUS_DEFAULT;
      this.geoLocationRadiusError = null;

      this.signInMode = 'any';
      this.multiSignalBadgeRequired = false;
      this.multiSignalWifiRequired = false;
      this.multiSignalSsoRequired = false;
      this.multiSignalMdmRequired = false;
      this.multiSignalGeoLocationRequired = false;
      this.multiSignalWindowMinutes = null;

      // Keep the snapshots in sync with the reset draft state so the tabs aren't spuriously dirty.
      this.persistedSignIn = this.signInDraft;
      this.persistedSignOut = this.signOutDraft;
      this.persistedMultiSignalPolicy = this.multiSignalPolicyDraft;
    } finally {
      this.isLoading = false;
    }
  }

  async loadLocationAutoSignOut(locationId: string): Promise<void> {
    if (!locationId) {
      this.locationAutoSignOutEnabled = false;
      return;
    }
    try {
      // `reload: true` — the application adapter never background-reloads records
      // (adapters/application.js#shouldBackgroundReloadRecord), so a cached Location could
      // carry a stale `autoSignOutAtMinutesSinceMidnight`. Force a fresh fetch so the
      // tooltip reflects the location's current setting. Matches the convention used by
      // other location loads that need fresh data (e.g. visitors/settings/location/route).
      const location = await this.store.findRecord('location', locationId, { reload: true });
      this.locationAutoSignOutEnabled = location.autoSignOutAtMinutesSinceMidnight !== null;
    } catch (_error) {
      // The tooltip is purely informational — never block the page on this.
      this.locationAutoSignOutEnabled = false;
    }
  }

  // PUTs the full presence-settings document for one tab's Save. The active `section` contributes its
  // draft; the other section is sent from its *persisted* snapshot, so unsaved edits on the other tab
  // are never persisted. Business hours is read-only here (edited on the per-location Location settings
  // page) and is always sent from its persisted snapshot so a Sign-in/Sign-out save never clears it.
  // The endpoint requires `settings` and treats an omitted business-hours as a delete, so we always
  // send all blocks (the `?? draft` fallbacks only matter before the first load). On success we promote
  // the saved section's snapshot so the tab goes clean.
  async savePresenceSettings(section: 'sign-in' | 'sign-out'): Promise<void> {
    if (!this.selectedLocationId) return;

    try {
      this.error = null;

      // Pulled out of the ternaries below so the `??` fallback isn't nested inside `?:` — Prettier
      // 3.1 (pre-commit hook) and 3.3 (CI) format that nesting differently, which fights on every commit.
      const signInFallback = this.persistedSignIn ?? this.signInDraft;
      const signOutFallback = this.persistedSignOut ?? this.signOutDraft;
      const multiSignalFallback = this.persistedMultiSignalPolicy ?? this.multiSignalPolicyDraft;
      // Per-signal settings (incl. geofence radius) are edited only in Allow-any mode. Send the live
      // draft when saving the Sign-in tab in that mode; a Multi-signal Sign-in save leaves the
      // per-signal settings at their persisted snapshot but still overlays the (both-mode-editable)
      // after-hours toggle. Other tabs send the persisted snapshot untouched.
      let settings: SettingsPayload;
      if (section === 'sign-in') {
        settings = this.signInMode === 'any' ? this.signInDraft : this.settingsWithAfterHours(signInFallback);
      } else {
        settings = signInFallback;
      }
      const signoutSettings = section === 'sign-out' ? this.signOutDraft : signOutFallback;
      // Business hours is edited elsewhere now; always carry the persisted snapshot so this Save
      // preserves the stored config rather than clearing it.
      const businessHours = this.persistedBusinessHours;
      // The multi-signal policy lives on the Sign-in tab; other tabs send the persisted snapshot so
      // their Save never alters it.
      const multiSignalPolicy = section === 'sign-in' ? this.multiSignalPolicyDraft : multiSignalFallback;

      // Stamp the location's IANA timezone at save time so the local HH:mm windows are self-contained
      // for downstream consumers (derived from the location, kept out of the draft/dirty-diff). A null
      // business-hours payload clears the stored config (matches the endpoint's PUT-replace semantics).
      const businessHoursPayload = businessHours
        ? { ...businessHours, timezone: this.selectedLocation?.location.timezone }
        : null;

      const attributes: Record<string, unknown> = {
        settings,
        'signout-settings': signoutSettings,
        // Top-level: business hours is independent of sign-out settings.
        'business-hours': businessHoursPayload,
      };
      // Only write the multi-signal policy when the feature is enabled. When off, omit the
      // block so a dark UI can't touch a stored policy — the backend keeps the existing value on omit.
      if (this.isMultiSignalPolicyEnabled) {
        attributes['multi-signal-policy'] = multiSignalPolicy;
      }

      const url = urlBuilder.presenceSettingsUrl(this.selectedLocationId);
      await this.ajax.request(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          data: {
            type: 'presence-settings',
            attributes,
          },
        },
      });

      // Promote only the saved section's snapshot to its draft. Promote exactly what was sent
      // (`settings`): in Allow-any that's the live draft; in Multi-signal it's the persisted per-signal
      // settings with the after-hours overlay, so the after-hours change sticks (and isn't re-flagged
      // dirty) while the uneditable per-signal settings stay put.
      if (section === 'sign-in') {
        this.persistedSignIn = settings;
        this.persistedMultiSignalPolicy = this.multiSignalPolicyDraft;
      } else {
        this.persistedSignOut = this.signOutDraft;
      }

      this.flashMessages.showAndHideFlash('success', 'Presence settings saved');
    } catch (_error) {
      this.error = 'Failed to save presence settings';
      this.flashMessages.showAndHideFlash('error', 'Failed to save presence settings');
    }
  }
}

export default LocationOverviewManageAccountSettingsController;
